<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" @click="close" />

        <!-- Modal Panel -->
        <div class="relative transform overflow-hidden rounded-xl bg-white shadow-2xl sm:w-full flex flex-col" style="width: 560px; max-height: 90vh; max-width: 95vw;">
          <!-- Header -->
          <div class="flex-shrink-0 border-b border-gray-100 px-6 py-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600">
                  <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Generate Before/After Images
                  </h3>
                  <p class="text-sm text-gray-500">
                    {{ stepDescription }}
                  </p>
                </div>
              </div>
              <button type="button" @click="close" class="text-gray-400 hover:text-gray-500">
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto min-h-0 px-6 py-4">
            <!-- Options Form -->
            <div v-if="step === 'options'" class="space-y-4">
              <!-- Provider Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">AI Provider</label>
                <div class="grid grid-cols-2 gap-3">
                  <button
                    v-for="p in providers"
                    :key="p.value"
                    type="button"
                    @click="selectedProvider = p.value"
                    :class="[
                      'relative flex cursor-pointer items-center rounded-lg border-2 p-3 text-left transition-all',
                      selectedProvider === p.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    ]">
                    <div class="flex items-center gap-3">
                      <div :class="['h-8 w-8 rounded-lg flex items-center justify-center', p.bgColor]">
                        <span :class="['text-sm font-semibold', p.textColor]">{{ p.icon }}</span>
                      </div>
                      <div>
                        <p class="text-sm font-medium text-gray-900">
                          {{ p.name }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ p.description }}
                        </p>
                      </div>
                    </div>
                    <div v-if="selectedProvider === p.value" class="absolute top-2 right-2 h-4 w-4 rounded-full bg-purple-500 flex items-center justify-center">
                      <svg class="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 6.586 3.707 5.293z" />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Style Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Style</label>
                <select v-model="selectedStyle" class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm">
                  <option v-for="style in styles" :key="style.value" :value="style.value">
                    {{ style.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Preview Step -->
            <div v-else-if="step === 'preview'" class="space-y-4">
              <!-- Summary -->
              <div class="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div class="flex items-center gap-3">
                  <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <span class="text-xl font-bold text-purple-600">{{ imageTargets.length }}</span>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-purple-900">
                      Images to generate
                    </p>
                    <p class="text-xs text-purple-600">
                      Using {{ selectedProvider === 'gemini' ? 'Google Imagen 3' : 'OpenAI DALL-E 3' }} &bull; {{ selectedStyle }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Items List -->
              <div>
                <h4 class="text-sm font-medium text-gray-700 mb-2">
                  Items to Generate
                </h4>
                <div class="space-y-2 max-h-64 overflow-y-auto">
                  <div
                    v-for="(target, idx) in imageTargets"
                    :key="idx"
                    class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                      <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span :class="['px-2 py-0.5 text-xs font-medium rounded-full', target.context?.image_type === 'before' ? 'bg-gray-200 text-gray-700' : 'bg-purple-100 text-purple-700']">
                          {{ target.context?.image_type === 'before' ? 'Before' : 'After' }}
                        </span>
                        <span class="text-sm text-gray-700 truncate">{{ target.context?.item_name || 'Item ' + (target.array_index + 1) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress -->
            <div v-else-if="step === 'progress'" class="space-y-4">
              <!-- Progress Bar -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-gray-700">
                    <template v-if="isComplete">
                      <span class="text-green-600 font-medium inline-flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Complete!
                      </span>
                    </template>
                    <template v-else>Processing images...</template>
                  </span>
                  <span class="text-sm font-bold text-purple-600">{{ progressPercent }}%</span>
                </div>
                <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full transition-all duration-500"
                    :style="{ width: progressPercent + '%' }" />
                </div>
                <div class="flex justify-between mt-1 text-xs text-gray-500">
                  <span>{{ completedCount }} of {{ totalImages }} completed</span>
                  <span v-if="failedCount > 0" class="text-red-500">{{ failedCount }} failed</span>
                </div>
              </div>

              <!-- Item Progress List -->
              <div class="space-y-2 max-h-48 overflow-y-auto">
                <div
                  v-for="(target, idx) in imageTargets"
                  :key="idx"
                  :class="[
                    'flex items-center gap-3 p-3 rounded-lg transition-all',
                    getItemBgClass(target)
                  ]">
                  <div :class="['flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', getItemIconBgClass(target)]">
                    <svg v-if="target.status === 'waiting'" class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <svg v-else-if="target.status === 'generating'" class="w-4 h-4 text-purple-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <svg v-else-if="target.status === 'completed'" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <svg v-else-if="target.status === 'failed'" class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span :class="['px-2 py-0.5 text-xs font-medium rounded-full', target.context?.image_type === 'before' ? 'bg-gray-100 text-gray-700' : 'bg-purple-100 text-purple-700']">
                        {{ target.context?.image_type === 'before' ? 'Before' : 'After' }}
                      </span>
                      <span class="text-sm text-gray-700 truncate">{{ target.context?.item_name || 'Item ' + (target.array_index + 1) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex justify-between items-center">
            <div class="text-sm text-gray-500">
              <span v-if="step === 'options'">Select options and preview</span>
              <span v-else-if="step === 'preview'">Est. ~{{ estimatedMinutes }} min</span>
              <span v-else-if="isComplete" class="text-green-600">
                {{ completedCount }} generated{{ failedCount > 0 ? `, ${failedCount} failed` : '' }}
              </span>
              <span v-else-if="errorMessage" class="text-red-600">{{ errorMessage }}</span>
            </div>
            <div class="flex gap-3">
              <!-- Cancel/Close button -->
              <button
                v-if="step !== 'progress' || isComplete"
                type="button"
                @click="close"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                {{ isComplete ? 'Close' : 'Cancel' }}
              </button>

              <!-- Options: Preview button -->
              <button
                v-if="step === 'options'"
                type="button"
                @click="previewGeneration"
                :disabled="isLoading"
                class="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2">
                <svg v-if="isLoading" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {{ isLoading ? 'Analyzing...' : 'Preview' }}
              </button>

              <!-- Preview: Back and Generate buttons -->
              <template v-if="step === 'preview'">
                <button
                  type="button"
                  @click="step = 'options'"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button
                  type="button"
                  @click="startGeneration"
                  :disabled="isLoading"
                  class="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2">
                  <svg v-if="isLoading" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  {{ isLoading ? 'Starting...' : `Generate ${imageTargets.length} Images` }}
                </button>
              </template>

              <!-- Progress complete: Done button -->
              <button
                v-if="step === 'progress' && isComplete"
                type="button"
                @click="close"
                class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'

const props = defineProps({
  websiteId: { type: String, required: true },
  pageId: { type: String, required: true }
})

const emit = defineEmits(['close'])

// State
const isOpen = ref(false)
const isLoading = ref(false)
const step = ref('options') // options, preview, progress
const errorMessage = ref('')
const currentJobId = ref(null)
const subscription = ref(null)

// Block info
const blockId = ref(null)
const missingCount = ref(0)

// Form state
const selectedProvider = ref('gemini')
const selectedStyle = ref('photorealistic')

// Progress
const imageTargets = ref([])
const totalImages = ref(0)
const completedCount = ref(0)
const failedCount = ref(0)

// Options
const providers = [
  { value: 'gemini', name: 'Google Imagen 3', description: '~15s per image', icon: 'G', bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
  { value: 'openai', name: 'OpenAI DALL-E 3', description: '~5s per image', icon: 'O', bgColor: 'bg-green-100', textColor: 'text-green-600' }
]

const styles = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'digital_art', label: 'Digital Art' },
  { value: 'minimalist', label: 'Minimalist' }
]

// Computed
const stepDescription = computed(() => {
  if (step.value === 'options') return 'Configure generation options'
  if (step.value === 'preview') return `Review ${imageTargets.value.length} images to generate`
  if (isComplete.value) return 'Generation complete'
  return `Generating ${totalImages.value} images...`
})

const progressPercent = computed(() => {
  if (totalImages.value === 0) return 0
  return Math.round(((completedCount.value + failedCount.value) / totalImages.value) * 100)
})

const isComplete = computed(() => totalImages.value > 0 && (completedCount.value + failedCount.value) >= totalImages.value)

const estimatedMinutes = computed(() => {
  const delay = selectedProvider.value === 'gemini' ? 15 : 5
  const count = imageTargets.value.length || missingCount.value
  return Math.max(1, Math.ceil((count * delay) / 60))
})

// Event listener for opening modal
function handleOpenModal(event) {
  const { blockId: bid, missingCount: count } = event.detail
  open(bid, count)
}

onMounted(() => {
  window.addEventListener('open-before-after-modal', handleOpenModal)
})

onUnmounted(() => {
  window.removeEventListener('open-before-after-modal', handleOpenModal)
  if (subscription.value) {
    subscription.value.unsubscribe()
  }
})

// Methods
function open(bid, count) {
  blockId.value = bid
  missingCount.value = count
  isOpen.value = true
  step.value = 'options'
  errorMessage.value = ''
  imageTargets.value = []
  currentJobId.value = null
  completedCount.value = 0
  failedCount.value = 0
}

function close() {
  const shouldReload = step.value === 'progress' && isComplete.value && completedCount.value > 0

  isOpen.value = false
  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
  emit('close')

  if (shouldReload) {
    setTimeout(() => window.location.reload(), 100)
  }
}

async function previewGeneration() {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const url = `/admin/websites/${props.websiteId}/pages/${props.pageId}/generate_before_after_images`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        block_id: blockId.value,
        provider: selectedProvider.value,
        style: selectedStyle.value,
        preview: true
      })
    })

    const data = await response.json()

    if (data.success) {
      if (data.total === 0) {
        errorMessage.value = 'All images already present'
        return
      }

      currentJobId.value = data.job_id
      totalImages.value = data.total
      imageTargets.value = (data.targets || []).map((t) => ({
        ...t,
        status: 'waiting'
      }))
      step.value = 'preview'
    } else {
      errorMessage.value = data.error || 'Failed to analyze images'
    }
  } catch (error) {
    console.error('Failed to preview:', error)
    errorMessage.value = 'Failed to analyze images'
  } finally {
    isLoading.value = false
  }
}

async function startGeneration() {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // If we already have a job from preview, start it
    // Otherwise create a new job
    const url = `/admin/websites/${props.websiteId}/pages/${props.pageId}/generate_before_after_images`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        block_id: blockId.value,
        provider: selectedProvider.value,
        style: selectedStyle.value,
        job_id: currentJobId.value // Pass existing job ID to start it
      })
    })

    const data = await response.json()

    if (data.success) {
      if (data.total === 0) {
        errorMessage.value = 'All images already present'
        return
      }

      currentJobId.value = data.job_id
      totalImages.value = data.total
      // Update targets with generating status for first item
      imageTargets.value = (data.targets || imageTargets.value).map((t, idx) => ({
        ...t,
        status: idx === 0 ? 'generating' : 'waiting'
      }))
      step.value = 'progress'
      subscribeToProgress()
    } else {
      errorMessage.value = data.error || 'Failed to start generation'
    }
  } catch (error) {
    console.error('Failed to start generation:', error)
    errorMessage.value = 'Failed to start generation'
  } finally {
    isLoading.value = false
  }
}

function subscribeToProgress() {
  if (subscription.value) {
    subscription.value.unsubscribe()
  }

  subscription.value = consumer.subscriptions.create(
    { channel: 'WebsiteGenerationChannel', website_id: props.websiteId },
    {
      connected: () => console.log('[BeforeAfter] Connected to generation channel'),
      disconnected: () => console.log('[BeforeAfter] Disconnected'),
      received: (data) => handleChannelMessage(data)
    }
  )
}

function handleChannelMessage(data) {
  console.log('[BeforeAfter] Received:', data.event, data)

  if (data.job_id !== currentJobId.value) return

  if (data.event === 'bulk_image_item_completed') {
    // Use nullish coalescing to handle 0 values correctly
    completedCount.value = data.completed ?? completedCount.value
    failedCount.value = data.failed ?? failedCount.value

    // Find and update completed target using array_index from broadcast
    const target = imageTargets.value.find((t) => t.block_id === data.block_id &&
             t.field_name === data.field_name &&
             t.array_index === data.array_index)
    if (target) {
      target.status = data.success ? 'completed' : 'failed'
    }

    // Mark next as generating
    const nextIndex = completedCount.value + failedCount.value
    if (nextIndex < imageTargets.value.length) {
      imageTargets.value[nextIndex].status = 'generating'
    }
  }
}

function getItemBgClass(target) {
  switch (target.status) {
    case 'generating': return 'bg-purple-50'
    case 'completed': return 'bg-green-50'
    case 'failed': return 'bg-red-50'
    default: return 'bg-gray-50'
  }
}

function getItemIconBgClass(target) {
  switch (target.status) {
    case 'generating': return 'bg-purple-100'
    case 'completed': return 'bg-green-100'
    case 'failed': return 'bg-red-100'
    default: return 'bg-gray-200'
  }
}

defineExpose({ open, close })
</script>
